import PropTypes from 'prop-types';
import DateAndTime from 'date-and-time';
import { sortByKeyAsNumber } from 'utils/common';

/**
 * Determines if the fund has enough financial data to show the chart.
 *
 * @param {array} aData - Fund financial history data.
 * @returns {boolean}
 */
export const fnHasFinancialChartData = (aData) => {
  let bHasChartData = false;
  const oFiscalYearData = aData[0];
  const nStartAmount = parseFloat(oFiscalYearData?.FISCAL_YEAR_BEGINNING_VALUE);
  const nEndAmount = parseFloat(oFiscalYearData?.FISCAL_YEAR_ENDING_VALUE);
  if (
    oFiscalYearData?.VALUE_OVER_TIME.length > 1 ||
    (nStartAmount > 0 && nEndAmount > 0)
  ) {
    bHasChartData = true;
  }
  return bHasChartData;
};

export const recipientPropType = PropTypes.shape({
  BIOGRAPHY: PropTypes.shape({
    NOTEID: PropTypes.string,
    NOTE: PropTypes.string,
    DATEADDED: PropTypes.string,
  }),
  FIRSTNAME: PropTypes.string,
  HAS_PHOTO: PropTypes.bool,
  LASTNAME: PropTypes.string,
  LOOKUPID: PropTypes.string,
  NAME: PropTypes.string,
  STATUS: PropTypes.string,
  THANKYOUNOTES: PropTypes.array,
});

/**
 * Flattens a financial data array into an array of VALUE_OVER_TIME objects.
 * @param {Array} aFinancialData - Array of financial data objects.
 * @returns {Array} Flattened array of VALUE_OVER_TIME objects.
 */
const fnFlattenValueOverTime = (aFinancialData) => {
  if (!Array.isArray(aFinancialData)) {
    return [];
  }
  return aFinancialData
    .filter((oItem) => Array.isArray(oItem.VALUE_OVER_TIME))
    .flatMap((oItem) => oItem.VALUE_OVER_TIME);
};

/**
 * Finds the most recent fiscal year end date (default: June 29) from an array of VALUE_OVER_TIME objects.
 * @param {Array} aValueOverTime - Array of VALUE_OVER_TIME objects.
 * @returns {string|null} Formatted date string or null if not found.
 */
const fnGetMostRecentFiscalYearEndDate = (aValueOverTime) => {
  const aFiscalYearEndItems = aValueOverTime.filter(
    (oItem) => oItem.END_DATE && oItem.END_DATE.includes('-06-29')
  );
  if (aFiscalYearEndItems.length > 0) {
    aFiscalYearEndItems.sort(
      (oA, oB) => new Date(oB.END_DATE) - new Date(oA.END_DATE)
    );
    const oEndDate = new Date(aFiscalYearEndItems[0].END_DATE);
    oEndDate.setDate(30); // Move to next day if needed
    return DateAndTime.format(oEndDate, 'MMMM D, YYYY');
  }
  return null;
};

/**
 * Finds the earliest fiscal year start date (default: July 1) from an array of VALUE_OVER_TIME objects
 * where DISTRIBUTIONS > 0 and REINVESTMENTS == 0.
 * @param {Array} aValueOverTime - Array of VALUE_OVER_TIME objects.
 * @returns {string|null} Formatted date string or null if not found.
 */
const fnGetEarliestFiscalYearStartDateWithDistributions = (aValueOverTime) => {
  let sEarliestFiscalYearStartDateWithDistributions = null;
  const aFiscalYearStartItems = aValueOverTime.filter((oItem) => {
    const bIsFiscalYearStart =
      oItem.START_DATE && oItem.START_DATE.includes('-07-01');
    const nDistributions = Math.abs(parseFloat(oItem.DISTRIBUTIONS));
    const nReinvestments = parseFloat(oItem.REINVESTMENTS);
    return bIsFiscalYearStart && nDistributions > 0 && nReinvestments === 0;
  });
  if (aFiscalYearStartItems.length > 0) {
    aFiscalYearStartItems.sort(
      (oA, oB) => new Date(oA.START_DATE) - new Date(oB.START_DATE)
    );
    sEarliestFiscalYearStartDateWithDistributions = DateAndTime.format(
      new Date(aFiscalYearStartItems[0].START_DATE),
      'MMMM D, YYYY'
    );
  }
  return sEarliestFiscalYearStartDateWithDistributions;
};

export const fnBuildTotalDistributionsInfo = (aFinancials, sFundName) => {
  let sDistributionInfo = '';
  let sStartDate = null;
  let sEndDate = null;

  const aAllValueOverTime = fnFlattenValueOverTime(aFinancials);

  // Calculate start date from quarterly data if available
  let sQuarterlyStartDate = null;
  if (aAllValueOverTime.length > 0) {
    // Fund has quarterly financial data
    sQuarterlyStartDate =
      fnGetEarliestFiscalYearStartDateWithDistributions(aAllValueOverTime);
    sEndDate = fnGetMostRecentFiscalYearEndDate(aAllValueOverTime);
  }

  // Calculate start date from annual data if available
  let sAnnualStartDate = null;
  if (aFinancials.length > 0) {
    // Fund has annual financial data
    const aChronologicalFinancials = sortByKeyAsNumber(
      aFinancials,
      'FISCALYEAR',
      true
    );
    sAnnualStartDate = `July 1, ${aChronologicalFinancials[0].FISCALYEAR - 1}`;

    // Use annual end date if quarterly data didn't provide one
    if (!sEndDate) {
      sEndDate = `June 30, ${
        aChronologicalFinancials[aChronologicalFinancials.length - 1].FISCALYEAR
      }`;
    }
  }

  // Some funds started out with only annual data, then switched to quarterly data.
  // Use the earliest start date available.
  if (sQuarterlyStartDate && sAnnualStartDate) {
    const oQuarterlyDate = new Date(sQuarterlyStartDate);
    const oAnnualDate = new Date(sAnnualStartDate);
    sStartDate =
      oQuarterlyDate < oAnnualDate ? sQuarterlyStartDate : sAnnualStartDate;
  } else {
    sStartDate = sQuarterlyStartDate || sAnnualStartDate;
  }

  if (sStartDate && sEndDate) {
    sDistributionInfo = `The total distributions amount for the ${sFundName} is based on available financial data from ${sStartDate} to ${sEndDate}.`;
  }
  return sDistributionInfo;
};

export default {};
